import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { userApi, getMediaUrl } from '../api';
import styles from './Settings.module.css';

// Wrapper-Funktionen
const updateUserSettings = (settings) => userApi.updateSettings(settings);
const updatePrivacySettings = (privacy) => userApi.updatePrivacy(privacy);

const Settings = ({ setLoggedIn }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('appearance');

  // Settings (API v1.2 snake_case) - PUT ist kein Partial-Update, daher
  // wird hier immer das vollständige Objekt gehalten und gesendet
  const [settings, setSettings] = useState({
    newsletter: false,
    email_notifications: true,
    push_notifications: true,
    new_release_alerts: true,
    wishlist_alerts: true,
    language: 'de',
    theme: 'dark',
    profile_public: false,
    discover_personalized: true,
  });

  // Privacy settings (API v1.2 snake_case) - ebenfalls kein Partial-Update
  const [privacy, setPrivacy] = useState({
    show_profile: true,
    show_library: true,
    show_wishlist: true,
    show_stats: true,
    show_manga_count: true,
    show_band_count: true,
    show_favorite_manga: true,
    show_read_manga: true,
  });

  // Passwort-Änderung State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwError, setPwError] = useState(null);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Load settings
  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [meResult, settingsResult, privacyResult] = await Promise.all([
        userApi.getProfile(),
        userApi.getSettings(),
        userApi.getPrivacy()
      ]);

      if (meResult.success) {
        setUser(meResult.data);
      }

      if (settingsResult.success && settingsResult.data) {
        const s = settingsResult.data;

        setSettings({
          newsletter: s.newsletter ?? false,
          email_notifications: s.email_notifications ?? true,
          push_notifications: s.push_notifications ?? true,
          new_release_alerts: s.new_release_alerts ?? true,
          wishlist_alerts: s.wishlist_alerts ?? true,
          language: s.language ?? 'de',
          theme: s.theme ?? 'dark',
          profile_public: s.profile_public ?? false,
          discover_personalized: s.discover_personalized ?? true,
        });

        if (s.theme) {
          document.documentElement.setAttribute('data-theme', s.theme);
        }
      }

      if (privacyResult.success && privacyResult.data) {
        setPrivacy({
          show_profile: privacyResult.data.show_profile ?? true,
          show_library: privacyResult.data.show_library ?? true,
          show_wishlist: privacyResult.data.show_wishlist ?? true,
          show_stats: privacyResult.data.show_stats ?? true,
          show_manga_count: privacyResult.data.show_manga_count ?? true,
          show_band_count: privacyResult.data.show_band_count ?? true,
          show_favorite_manga: privacyResult.data.show_favorite_manga ?? true,
          show_read_manga: privacyResult.data.show_read_manga ?? true,
        });
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
      setError(t('settings.loadError'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Show success message temporarily
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // Handle theme change (PUT ist kein Partial-Update -> volles Settings-Objekt senden)
  const handleThemeChange = async (newTheme) => {
    const previousTheme = settings.theme;
    const updated = { ...settings, theme: newTheme };
    setSettings(updated);
    document.documentElement.setAttribute('data-theme', newTheme);

    try {
      setSaving(true);
      const response = await updateUserSettings(updated);
      if (response.success) {
        showSuccess(t('settings.themeUpdated'));
      } else {
        setSettings(prev => ({ ...prev, theme: previousTheme }));
        document.documentElement.setAttribute('data-theme', previousTheme);
        setError(t('settings.themeSaveError'));
      }
    } catch (err) {
      console.error('Failed to save theme:', err);
      setSettings(prev => ({ ...prev, theme: previousTheme }));
      document.documentElement.setAttribute('data-theme', previousTheme);
      setError(t('settings.themeSaveError'));
    } finally {
      setSaving(false);
    }
  };

  // Handle notification toggle (PUT ist kein Partial-Update -> volles Settings-Objekt senden)
  const handleNotificationChange = async (key) => {
    const newValue = !settings[key];
    const updated = { ...settings, [key]: newValue };
    setSettings(updated);

    try {
      setSaving(true);
      const response = await updateUserSettings(updated);
      if (response.success) {
        showSuccess(t('settings.notificationsSaved'));
      } else {
        setSettings(prev => ({ ...prev, [key]: !newValue }));
        setError(t('settings.settingSaveError'));
      }
    } catch (err) {
      console.error('Failed to save notification settings:', err);
      setSettings(prev => ({ ...prev, [key]: !newValue }));
      setError(t('settings.settingSaveError'));
    } finally {
      setSaving(false);
    }
  };

  // Handle privacy toggle
  const handlePrivacyChange = async (key) => {
    const newValue = !privacy[key];
    setPrivacy(prev => ({ ...prev, [key]: newValue }));

    try {
      setSaving(true);
      const response = await updatePrivacySettings({
        ...privacy,
        [key]: newValue
      });
      if (response.success) {
        showSuccess(t('settings.privacySaved'));
      } else {
        setPrivacy(prev => ({ ...prev, [key]: !newValue }));
        setError(t('settings.settingSaveError'));
      }
    } catch (err) {
      console.error('Failed to save privacy settings:', err);
      setPrivacy(prev => ({ ...prev, [key]: !newValue }));
      setError(t('settings.settingSaveError'));
    } finally {
      setSaving(false);
    }
  };

  // Handle password change (PATCH /users/me/password)
  const handlePasswordChange = async () => {
    setPwError(null);
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPwError(t('settings.pwFillAll'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError(t('settings.pwMismatch'));
      return;
    }
    if (newPassword.length < 8) {
      setPwError(t('settings.pwTooShort'));
      return;
    }
    try {
      setSaving(true);
      const response = await userApi.changePassword(currentPassword, newPassword);
      if (response.success) {
        showSuccess(t('settings.pwChanged'));
        setShowPasswordResetModal(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const code = response.error?.code;
        if (code === 'INVALID_PASSWORD' || code === 'UNAUTHORIZED') {
          setPwError(t('settings.pwCurrentWrong'));
        } else if (code === 'VALIDATION_ERROR') {
          setPwError(t('settings.pwRequirements'));
        } else {
          setPwError(response.error?.message || t('settings.pwChangeError'));
        }
      }
    } catch (err) {
      setPwError(t('settings.pwChangeError'));
    } finally {
      setSaving(false);
    }
  };

  const deleteConfirmWord = t('settings.deleteConfirmWord');

  // Eigene Daten exportieren (§7) - lädt alles als JSON-Datei herunter
  const handleExportData = async () => {
    setError(null);
    setSuccessMessage(null);
    try {
      setExporting(true);
      const response = await userApi.exportData();
      if (response.success) {
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `mangashelf-export-${new Date().toISOString().slice(0, 10)}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setSuccessMessage(t('settings.exportDataSuccess'));
      } else {
        setError(response.error?.message || t('settings.exportDataError'));
      }
    } catch (err) {
      console.error('Failed to export data:', err);
      setError(t('settings.exportDataError'));
    } finally {
      setExporting(false);
    }
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== deleteConfirmWord) {
      setError(t('settings.deleteConfirmHint', { word: deleteConfirmWord }));
      return;
    }

    try {
      setSaving(true);
      const response = await userApi.deleteAccount();
      if (response.success) {
        setShowDeleteAccountModal(false);
        setDeleteConfirmText('');
        if (setLoggedIn) setLoggedIn(false);
        navigate('/');
      } else {
        setError(response.error?.message || t('settings.deleteAccountError'));
      }
    } catch (err) {
      console.error('Failed to delete account:', err);
      setError(t('settings.deleteAccountError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.settingsPage}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>{t('settings.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.settingsPage}>
      <div className={styles.container}>
        <header className={styles.header}>
          <h1>{t('settings.title')}</h1>
          <p className={styles.subtitle}>{t('settings.subtitle')}</p>
        </header>

        {error && (
          <div className={styles.errorMessage}>
            <span className={styles.errorIcon}>⚠️</span>
            {error}
            <button onClick={() => setError(null)} className={styles.dismissButton}>×</button>
          </div>
        )}

        {successMessage && (
          <div className={styles.successMessage}>
            <span className={styles.successIcon}>✓</span>
            {successMessage}
          </div>
        )}

        {/* Tab Navigation */}
        <nav className={styles.tabNav}>
          <button
            className={`${styles.tabButton} ${activeTab === 'appearance' ? styles.active : ''}`}
            onClick={() => setActiveTab('appearance')}
          >
            <span className={styles.tabIcon}>🎨</span>
            {t('settings.tabAppearance')}
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'notifications' ? styles.active : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            <span className={styles.tabIcon}>🔔</span>
            {t('settings.tabNotifications')}
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'privacy' ? styles.active : ''}`}
            onClick={() => setActiveTab('privacy')}
          >
            <span className={styles.tabIcon}>🔒</span>
            {t('settings.tabPrivacy')}
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'account' ? styles.active : ''}`}
            onClick={() => setActiveTab('account')}
          >
            <span className={styles.tabIcon}>👤</span>
            {t('settings.tabAccount')}
          </button>
        </nav>

        {/* Tab Content */}
        <div className={styles.tabContent}>
          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('settings.tabAppearance')}</h2>

              <div className={styles.settingGroup}>
                <h3 className={styles.groupTitle}>{t('settings.colorScheme')}</h3>
                <p className={styles.groupDescription}>{t('settings.colorSchemeDesc')}</p>

                <div className={styles.themeSelector}>
                  <button
                    className={`${styles.themeOption} ${settings.theme === 'light' ? styles.active : ''}`}
                    onClick={() => handleThemeChange('light')}
                    disabled={saving}
                  >
                    <div className={styles.themePreview} data-theme="light">
                      <div className={styles.previewHeader}></div>
                      <div className={styles.previewContent}>
                        <div className={styles.previewCard}></div>
                        <div className={styles.previewCard}></div>
                      </div>
                    </div>
                    <span className={styles.themeName}>☀️ {t('settings.light')}</span>
                  </button>

                  <button
                    className={`${styles.themeOption} ${settings.theme === 'dark' ? styles.active : ''}`}
                    onClick={() => handleThemeChange('dark')}
                    disabled={saving}
                  >
                    <div className={styles.themePreview} data-theme="dark">
                      <div className={styles.previewHeader}></div>
                      <div className={styles.previewContent}>
                        <div className={styles.previewCard}></div>
                        <div className={styles.previewCard}></div>
                      </div>
                    </div>
                    <span className={styles.themeName}>🌙 {t('settings.dark')}</span>
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('settings.tabNotifications')}</h2>

              <div className={styles.settingGroup}>
                <h3 className={styles.groupTitle}>{t('settings.emailNotificationsGroup')}</h3>

                <div className={styles.toggleList}>
                  <label className={styles.toggleItem}>
                    <div className={styles.toggleInfo}>
                      <span className={styles.toggleLabel}>{t('settings.emailNotifications')}</span>
                      <span className={styles.toggleDescription}>{t('settings.emailNotificationsDesc')}</span>
                    </div>
                    <div className={styles.toggleSwitch}>
                      <input
                        type="checkbox"
                        checked={settings.email_notifications}
                        onChange={() => handleNotificationChange('email_notifications')}
                        disabled={saving}
                      />
                      <span className={styles.slider}></span>
                    </div>
                  </label>

                  <label className={styles.toggleItem}>
                    <div className={styles.toggleInfo}>
                      <span className={styles.toggleLabel}>{t('settings.newsletter')}</span>
                      <span className={styles.toggleDescription}>{t('settings.newsletterDesc')}</span>
                    </div>
                    <div className={styles.toggleSwitch}>
                      <input
                        type="checkbox"
                        checked={settings.newsletter}
                        onChange={() => handleNotificationChange('newsletter')}
                        disabled={saving}
                      />
                      <span className={styles.slider}></span>
                    </div>
                  </label>
                </div>
              </div>

              <div className={styles.settingGroup}>
                <h3 className={styles.groupTitle}>{t('settings.pushNotificationsGroup')}</h3>

                <div className={styles.toggleList}>
                  <label className={styles.toggleItem}>
                    <div className={styles.toggleInfo}>
                      <span className={styles.toggleLabel}>{t('settings.pushNotifications')}</span>
                      <span className={styles.toggleDescription}>{t('settings.pushNotificationsDesc')}</span>
                    </div>
                    <div className={styles.toggleSwitch}>
                      <input
                        type="checkbox"
                        checked={settings.push_notifications}
                        onChange={() => handleNotificationChange('push_notifications')}
                        disabled={saving}
                      />
                      <span className={styles.slider}></span>
                    </div>
                  </label>

                  <label className={styles.toggleItem}>
                    <div className={styles.toggleInfo}>
                      <span className={styles.toggleLabel}>{t('settings.newReleaseAlerts')}</span>
                      <span className={styles.toggleDescription}>{t('settings.newReleaseAlertsDesc')}</span>
                    </div>
                    <div className={styles.toggleSwitch}>
                      <input
                        type="checkbox"
                        checked={settings.new_release_alerts}
                        onChange={() => handleNotificationChange('new_release_alerts')}
                        disabled={saving}
                      />
                      <span className={styles.slider}></span>
                    </div>
                  </label>

                  <label className={styles.toggleItem}>
                    <div className={styles.toggleInfo}>
                      <span className={styles.toggleLabel}>{t('settings.wishlistAlerts')}</span>
                      <span className={styles.toggleDescription}>{t('settings.wishlistAlertsDesc')}</span>
                    </div>
                    <div className={styles.toggleSwitch}>
                      <input
                        type="checkbox"
                        checked={settings.wishlist_alerts}
                        onChange={() => handleNotificationChange('wishlist_alerts')}
                        disabled={saving}
                      />
                      <span className={styles.slider}></span>
                    </div>
                  </label>
                </div>
              </div>
            </section>
          )}

          {/* Privacy Tab */}
          {activeTab === 'privacy' && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('settings.tabPrivacy')}</h2>

              <div className={styles.settingGroup}>
                <h3 className={styles.groupTitle}>{t('settings.profileVisibility')}</h3>

                <div className={styles.toggleList}>
                  <label className={styles.toggleItem}>
                    <div className={styles.toggleInfo}>
                      <span className={styles.toggleLabel}>{t('settings.publicProfile')}</span>
                      <span className={styles.toggleDescription}>{t('settings.publicProfileDesc')}</span>
                    </div>
                    <div className={styles.toggleSwitch}>
                      <input
                        type="checkbox"
                        checked={privacy.show_profile}
                        onChange={() => handlePrivacyChange('show_profile')}
                        disabled={saving}
                      />
                      <span className={styles.slider}></span>
                    </div>
                  </label>

                  <label className={styles.toggleItem}>
                    <div className={styles.toggleInfo}>
                      <span className={styles.toggleLabel}>{t('settings.showLibrary')}</span>
                      <span className={styles.toggleDescription}>{t('settings.showLibraryDesc')}</span>
                    </div>
                    <div className={styles.toggleSwitch}>
                      <input
                        type="checkbox"
                        checked={privacy.show_library}
                        onChange={() => handlePrivacyChange('show_library')}
                        disabled={saving}
                      />
                      <span className={styles.slider}></span>
                    </div>
                  </label>

                  <label className={styles.toggleItem}>
                    <div className={styles.toggleInfo}>
                      <span className={styles.toggleLabel}>{t('settings.showWishlist')}</span>
                      <span className={styles.toggleDescription}>{t('settings.showWishlistDesc')}</span>
                    </div>
                    <div className={styles.toggleSwitch}>
                      <input
                        type="checkbox"
                        checked={privacy.show_wishlist}
                        onChange={() => handlePrivacyChange('show_wishlist')}
                        disabled={saving}
                      />
                      <span className={styles.slider}></span>
                    </div>
                  </label>

                  <label className={styles.toggleItem}>
                    <div className={styles.toggleInfo}>
                      <span className={styles.toggleLabel}>{t('settings.showStats')}</span>
                      <span className={styles.toggleDescription}>{t('settings.showStatsDesc')}</span>
                    </div>
                    <div className={styles.toggleSwitch}>
                      <input
                        type="checkbox"
                        checked={privacy.show_stats}
                        onChange={() => handlePrivacyChange('show_stats')}
                        disabled={saving}
                      />
                      <span className={styles.slider}></span>
                    </div>
                  </label>
                </div>
              </div>

              <div className={styles.settingGroup}>
                <h3 className={styles.groupTitle}>{t('settings.friendFieldsGroup')}</h3>
                <p className={styles.groupDescription}>{t('settings.friendFieldsDesc')}</p>

                <div className={styles.toggleList}>
                  <label className={styles.toggleItem}>
                    <div className={styles.toggleInfo}>
                      <span className={styles.toggleLabel}>{t('settings.showMangaCount')}</span>
                    </div>
                    <div className={styles.toggleSwitch}>
                      <input
                        type="checkbox"
                        checked={privacy.show_manga_count}
                        onChange={() => handlePrivacyChange('show_manga_count')}
                        disabled={saving}
                      />
                      <span className={styles.slider}></span>
                    </div>
                  </label>

                  <label className={styles.toggleItem}>
                    <div className={styles.toggleInfo}>
                      <span className={styles.toggleLabel}>{t('settings.showBandCount')}</span>
                    </div>
                    <div className={styles.toggleSwitch}>
                      <input
                        type="checkbox"
                        checked={privacy.show_band_count}
                        onChange={() => handlePrivacyChange('show_band_count')}
                        disabled={saving}
                      />
                      <span className={styles.slider}></span>
                    </div>
                  </label>

                  <label className={styles.toggleItem}>
                    <div className={styles.toggleInfo}>
                      <span className={styles.toggleLabel}>{t('settings.showFavoriteManga')}</span>
                    </div>
                    <div className={styles.toggleSwitch}>
                      <input
                        type="checkbox"
                        checked={privacy.show_favorite_manga}
                        onChange={() => handlePrivacyChange('show_favorite_manga')}
                        disabled={saving}
                      />
                      <span className={styles.slider}></span>
                    </div>
                  </label>

                  <label className={styles.toggleItem}>
                    <div className={styles.toggleInfo}>
                      <span className={styles.toggleLabel}>{t('settings.showReadManga')}</span>
                    </div>
                    <div className={styles.toggleSwitch}>
                      <input
                        type="checkbox"
                        checked={privacy.show_read_manga}
                        onChange={() => handlePrivacyChange('show_read_manga')}
                        disabled={saving}
                      />
                      <span className={styles.slider}></span>
                    </div>
                  </label>
                </div>
              </div>

              <div className={styles.settingGroup}>
                <h3 className={styles.groupTitle}>{t('settings.discoverGroup')}</h3>

                <div className={styles.toggleList}>
                  <label className={styles.toggleItem}>
                    <div className={styles.toggleInfo}>
                      <span className={styles.toggleLabel}>{t('settings.discoverPersonalized')}</span>
                      <span className={styles.toggleDescription}>{t('settings.discoverPersonalizedDesc')}</span>
                    </div>
                    <div className={styles.toggleSwitch}>
                      <input
                        type="checkbox"
                        checked={settings.discover_personalized}
                        onChange={() => handleNotificationChange('discover_personalized')}
                        disabled={saving}
                      />
                      <span className={styles.slider}></span>
                    </div>
                  </label>
                </div>
              </div>
            </section>
          )}

          {/* Account Tab */}
          {activeTab === 'account' && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('settings.tabAccount')}</h2>

              {user && (
                <div className={styles.accountInfo}>
                  <div className={styles.accountAvatar}>
                    {user.avatar_has && user.avatar_media_key ? (
                      <img src={getMediaUrl(user.avatar_media_key)} alt={user.username} />
                    ) : (
                      <div className={styles.avatarPlaceholder}>
                        {user.username?.charAt(0).toUpperCase() || '?'}
                      </div>
                    )}
                  </div>
                  <div className={styles.accountDetails}>
                    <h3>{user.username}</h3>
                    <p>{user.email}</p>
                  </div>
                </div>
              )}

              <div className={styles.settingGroup}>
                <h3 className={styles.groupTitle}>{t('settings.securityGroup')}</h3>

                <div className={styles.actionList}>
                  <div className={styles.actionItem}>
                    <div className={styles.actionInfo}>
                      <span className={styles.actionLabel}>{t('settings.changePassword')}</span>
                      <span className={styles.actionDescription}>{t('settings.changePasswordDesc')}</span>
                    </div>
                    <button
                      className={styles.actionButton}
                      onClick={() => setShowPasswordResetModal(true)}
                      disabled={saving}
                    >
                      {t('settings.resetPassword')}
                    </button>
                  </div>
                </div>
              </div>

              <div className={styles.settingGroup}>
                <h3 className={styles.groupTitle}>{t('settings.editProfileGroup')}</h3>

                <div className={styles.actionList}>
                  <div className={styles.actionItem}>
                    <div className={styles.actionInfo}>
                      <span className={styles.actionLabel}>{t('settings.editProfile')}</span>
                      <span className={styles.actionDescription}>{t('settings.editProfileDesc')}</span>
                    </div>
                    <button
                      className={styles.actionButton}
                      onClick={() => navigate('/profile')}
                    >
                      {t('settings.goToProfile')}
                    </button>
                  </div>
                </div>
              </div>

              <div className={styles.settingGroup}>
                <h3 className={styles.groupTitle}>{t('settings.dataExportGroup')}</h3>

                <div className={styles.actionList}>
                  <div className={styles.actionItem}>
                    <div className={styles.actionInfo}>
                      <span className={styles.actionLabel}>{t('settings.exportData')}</span>
                      <span className={styles.actionDescription}>{t('settings.exportDataDesc')}</span>
                    </div>
                    <button
                      className={styles.actionButton}
                      onClick={handleExportData}
                      disabled={exporting}
                    >
                      {exporting ? t('settings.exporting') : t('settings.exportData')}
                    </button>
                  </div>
                </div>
              </div>

              <div className={styles.settingGroup}>
                <h3 className={`${styles.groupTitle} ${styles.danger}`}>{t('settings.dangerZone')}</h3>

                <div className={styles.dangerZone}>
                  <div className={styles.dangerItem}>
                    <div className={styles.dangerInfo}>
                      <span className={styles.dangerLabel}>{t('settings.deleteAccount')}</span>
                      <span className={styles.dangerDescription}>
                        {t('settings.deleteAccountDesc')}
                      </span>
                    </div>
                    <button
                      className={styles.dangerButton}
                      onClick={() => setShowDeleteAccountModal(true)}
                      disabled={saving}
                    >
                      {t('settings.deleteAccount')}
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>

      {/* Passwort ändern Modal */}
      {showPasswordResetModal && (
        <div className={styles.modalOverlay} onClick={() => { setShowPasswordResetModal(false); setPwError(null); }}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h2>{t('settings.changePasswordModalTitle')}</h2>

            <div className={styles.formGroup} style={{marginBottom:'1rem'}}>
              <label className={styles.formLabel}>{t('settings.currentPassword')}</label>
              <input
                type="password"
                className={styles.formInput}
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                placeholder={t('settings.currentPassword')}
                autoComplete="current-password"
              />
            </div>
            <div className={styles.formGroup} style={{marginBottom:'1rem'}}>
              <label className={styles.formLabel}>{t('settings.newPassword')}</label>
              <input
                type="password"
                className={styles.formInput}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder={t('settings.newPasswordPlaceholder')}
                autoComplete="new-password"
              />
            </div>
            <div className={styles.formGroup} style={{marginBottom:'1rem'}}>
              <label className={styles.formLabel}>{t('settings.confirmNewPassword')}</label>
              <input
                type="password"
                className={styles.formInput}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder={t('settings.repeatPassword')}
                autoComplete="new-password"
              />
            </div>

            {pwError && (
              <div className={styles.errorMessage} style={{marginBottom:'1rem'}}>{pwError}</div>
            )}

            <div className={styles.modalActions}>
              <button
                className={styles.cancelButton}
                onClick={() => { setShowPasswordResetModal(false); setPwError(null); }}
                disabled={saving}
              >
                {t('settings.cancel')}
              </button>
              <button
                className={styles.confirmButton}
                onClick={handlePasswordChange}
                disabled={saving}
              >
                {saving ? t('settings.saving') : t('settings.changePasswordModalTitle')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteAccountModal && (
        <div className={styles.modalOverlay} onClick={() => setShowDeleteAccountModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h2 className={styles.dangerTitle}>⚠️ {t('settings.deleteAccount')}</h2>
            <p>
              <Trans i18nKey="settings.deleteAccountWarning" components={[<strong key="0" />]} />
            </p>
            <ul className={styles.deleteWarningList}>
              <li>{t('settings.deleteWarnLibrary')}</li>
              <li>{t('settings.deleteWarnWishlist')}</li>
              <li>{t('settings.deleteWarnFriends')}</li>
              <li>{t('settings.deleteWarnStats')}</li>
            </ul>

            <div className={styles.confirmInput}>
              <label>{t('settings.deleteConfirmLabel', { word: deleteConfirmWord })}</label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={deleteConfirmWord}
              />
            </div>

            <div className={styles.modalActions}>
              <button
                className={styles.cancelButton}
                onClick={() => {
                  setShowDeleteAccountModal(false);
                  setDeleteConfirmText('');
                }}
                disabled={saving}
              >
                {t('settings.cancel')}
              </button>
              <button
                className={styles.dangerConfirmButton}
                onClick={handleDeleteAccount}
                disabled={saving || deleteConfirmText !== deleteConfirmWord}
              >
                {saving ? t('settings.deleting') : t('settings.deleteAccount')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
